import {
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  autoUpdate,
  flip,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from "@floating-ui/react";
import * as React from "react";
import { PropsWithChildren, ReactNode, useMemo, useRef, useState } from "react";

import { Card } from "@design-system/Card/Card";

import { DropdownContext } from "./DropdownContext";
import { Option } from "./UI/Option/Option";

import classes from "./Dropdown.module.css";

type Props = PropsWithChildren<{
  trigger: ReactNode;
}>;

export const Dropdown = ({ children, trigger }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [flip()],
  });

  const elementsRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const handleSelect = React.useCallback((index: number | null) => {
    setSelectedIndex(index);
    setIsOpen(false);
  }, []);

  function handleTypeaheadMatch(index: number | null) {
    if (isOpen) {
      setActiveIndex(index);
    } else {
      handleSelect(index);
    }
  }

  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
  });
  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    selectedIndex,
    onMatch: handleTypeaheadMatch,
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "listbox" });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [listNav, typeahead, click, dismiss, role],
  );

  const dropdownContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      getItemProps,
      handleSelect,
    }),
    [activeIndex, selectedIndex, getItemProps, handleSelect],
  );

  return (
    <>
      <div ref={refs.setReference} tabIndex={0} {...getReferenceProps()}>
        {trigger}
      </div>
      <FloatingPortal>
        <DropdownContext.Provider value={dropdownContext}>
          {isOpen && (
            <FloatingFocusManager context={context} modal={false}>
              <div
                ref={refs.setFloating}
                style={{ ...floatingStyles, zIndex: 1001 }}
                {...getFloatingProps()}
              >
                <Card>
                  <div className={classes.list}>
                    <FloatingList
                      elementsRef={elementsRef}
                      labelsRef={labelsRef}
                    >
                      {children}
                    </FloatingList>
                  </div>
                </Card>
              </div>
            </FloatingFocusManager>
          )}
        </DropdownContext.Provider>
      </FloatingPortal>
    </>
  );
};

Dropdown.Option = Option;
